import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Lock, Bell, BellOff, Plus, X, Clock, Trash2, Users, RotateCcw, ThumbsUp, ThumbsDown, Sparkles, Printer } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import CannedResponsePicker from '../components/tickets/CannedResponsePicker';
import { SlaPanel } from '../components/sla/SlaBadge';
import RichTextEditor from '../components/shared/RichTextEditor';
import RichTextDisplay from '../components/shared/RichTextDisplay';
import RatingWidget from '../components/tickets/RatingWidget';
import AttachmentsPanel from '../components/shared/Attachments';
import { parseApiDate } from '../lib/utils';
import { format, formatDistanceToNow } from 'date-fns';
import StatusBadge from '../components/tickets/StatusBadge';
import PriorityBadge from '../components/tickets/PriorityBadge';
import toast from 'react-hot-toast';

function formatMinutes(mins) {
  if (!mins) return '0m';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}

// ── Watchers Panel ────────────────────────────────────────────
function WatchersPanel({ ticketId }) {
  const [watchers, setWatchers]   = useState([]);
  const [addEmail, setAddEmail]   = useState('');
  const [addName, setAddName]     = useState('');
  const [adding, setAdding]       = useState(false);
  const [showForm, setShowForm]   = useState(false);

  useEffect(() => { fetchWatchers(); }, [ticketId]);

  const fetchWatchers = () =>
    api.get(`/tickets/${ticketId}/watchers`).then(d => setWatchers(d.watchers || []));

  const addWatcher = async () => {
    if (!addEmail.trim()) return;
    setAdding(true);
    try {
      await api.post(`/tickets/${ticketId}/watchers`, { email: addEmail.trim(), name: addName.trim() });
      toast.success('Watcher added');
      setAddEmail(''); setAddName(''); setShowForm(false);
      fetchWatchers();
    } catch (err) {
      toast.error(err.message || 'Failed to add watcher');
    } finally { setAdding(false); }
  };

  const removeWatcher = async (id) => {
    try {
      await api.delete(`/tickets/${ticketId}/watchers/${id}`);
      setWatchers(w => w.filter(x => x.id !== id));
    } catch (err) { toast.error('Failed to remove watcher'); }
  };

  const initials = (w) =>
    (w.name || w.email).split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase();

  return (
    <div className="card card-sm">
      <div className="flex justify-between items-center" style={{ marginBottom: 12 }}>
        <div style={{ fontWeight: 600, fontSize: 12.5, color: 'var(--text-secondary)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          <Users size={12} style={{ display:'inline', marginRight: 5 }} />Watchers
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => setShowForm(s => !s)} style={{ padding: '3px 6px' }}>
          <Plus size={13} />
        </button>
      </div>

      {showForm && (
        <div style={{ marginBottom: 12, padding: 10, background: 'var(--bg-secondary)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
          <div className="form-group" style={{ marginBottom: 8 }}>
            <input className="form-input" type="email" placeholder="Email address *"
              value={addEmail} onChange={e => setAddEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addWatcher()}
              style={{ fontSize: 12.5, padding: '6px 10px' }} />
          </div>
          <div className="form-group" style={{ marginBottom: 8 }}>
            <input className="form-input" type="text" placeholder="Name (optional)"
              value={addName} onChange={e => setAddName(e.target.value)}
              style={{ fontSize: 12.5, padding: '6px 10px' }} />
          </div>
          <div className="flex gap-2">
            <button className="btn btn-secondary btn-sm" onClick={() => { setShowForm(false); setAddEmail(''); setAddName(''); }}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={addWatcher} disabled={adding || !addEmail.trim()}>
              {adding ? 'Adding…' : 'Add'}
            </button>
          </div>
        </div>
      )}

      {watchers.length === 0 ? (
        <p className="text-muted text-sm">No watchers yet.</p>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {watchers.map(w => (
            <span key={w.id} className="watcher-chip" title={w.email}>
              <span style={{ width: 18, height: 18, borderRadius: '50%', background: 'var(--accent-light)', display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize: 9, fontWeight: 700, flexShrink: 0 }}>
                {initials(w)}
              </span>
              <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {w.name || w.email}
              </span>
              <button onClick={() => removeWatcher(w.id)} title="Remove">
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Time Tracking Panel ───────────────────────────────────────
function TimePanel({ ticketId }) {
  const [entries, setEntries]       = useState([]);
  const [totalMinutes, setTotal]    = useState(0);
  const [techTotals, setTechTotals] = useState([]);
  const [showForm, setShowForm]     = useState(false);
  const [form, setForm]             = useState({ hours: '', minutes: '', notes: '' });
  const [saving, setSaving]         = useState(false);

  useEffect(() => { fetchTime(); }, [ticketId]);

  const fetchTime = () =>
    api.get(`/tickets/${ticketId}/time`).then(d => {
      setEntries(d.entries || []);
      setTotal(d.totalMinutes || 0);
      setTechTotals(d.techTotals || []);
    });

  const logTime = async () => {
    const totalMins = (parseInt(form.hours || 0) * 60) + parseInt(form.minutes || 0);
    if (!totalMins) { toast.error('Enter time greater than 0'); return; }
    setSaving(true);
    try {
      await api.post(`/tickets/${ticketId}/time`, { minutes: totalMins, notes: form.notes });
      toast.success('Time logged');
      setForm({ hours: '', minutes: '', notes: '' });
      setShowForm(false);
      fetchTime();
    } catch (err) { toast.error('Failed to log time'); }
    finally { setSaving(false); }
  };

  const deleteEntry = async (id) => {
    try {
      await api.delete(`/tickets/${ticketId}/time/${id}`);
      fetchTime();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div className="card card-sm">
      <div className="flex justify-between items-center" style={{ marginBottom: 12 }}>
        <div className="flex items-center gap-2">
          <div style={{ fontWeight: 600, fontSize: 12.5, color: 'var(--text-secondary)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            <Clock size={12} style={{ display:'inline', marginRight: 5 }} />Time
          </div>
          {totalMinutes > 0 && (
            <span className="time-pill">{formatMinutes(totalMinutes)}</span>
          )}
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => setShowForm(s => !s)} style={{ padding: '3px 6px' }}>
          <Plus size={13} />
        </button>
      </div>

      {showForm && (
        <div style={{ marginBottom: 12, padding: 10, background: 'var(--bg-secondary)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
          <div className="flex gap-2" style={{ marginBottom: 8 }}>
            <div style={{ flex: 1 }}>
              <label className="form-label">Hours</label>
              <input className="form-input" type="number" min="0" placeholder="0"
                value={form.hours} onChange={e => setForm(f => ({ ...f, hours: e.target.value }))}
                style={{ fontSize: 12.5, padding: '6px 10px' }} />
            </div>
            <div style={{ flex: 1 }}>
              <label className="form-label">Minutes</label>
              <input className="form-input" type="number" min="0" max="59" placeholder="0"
                value={form.minutes} onChange={e => setForm(f => ({ ...f, minutes: e.target.value }))}
                style={{ fontSize: 12.5, padding: '6px 10px' }} />
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: 8 }}>
            <input className="form-input" placeholder="Notes (optional)"
              value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              style={{ fontSize: 12.5, padding: '6px 10px' }} />
          </div>
          <div className="flex gap-2">
            <button className="btn btn-secondary btn-sm" onClick={() => setShowForm(false)}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={logTime} disabled={saving}>
              {saving ? 'Saving…' : 'Log Time'}
            </button>
          </div>
        </div>
      )}

      {techTotals.length > 1 && (
        <div style={{ marginBottom: 12, padding: 10, background: 'var(--bg-secondary)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 6 }}>By Technician</div>
          {techTotals.map(t => (
            <div key={t.tech_id} className="flex justify-between items-center" style={{ fontSize: 12.5, padding: '3px 0' }}>
              <span>{t.tech_name || t.tech_email}</span>
              <span className="time-pill">{formatMinutes(t.minutes)}</span>
            </div>
          ))}
          <div className="flex justify-between items-center" style={{ fontSize: 12.5, fontWeight: 600, borderTop: '1px solid var(--border)', paddingTop: 6, marginTop: 6 }}>
            <span>Total</span>
            <span className="time-pill">{formatMinutes(totalMinutes)}</span>
          </div>
        </div>
      )}

      {entries.length === 0 ? (
        <p className="text-muted text-sm">No time logged yet.</p>
      ) : (
        <div>
          {entries.map(e => (
            <div key={e.id} className="time-entry">
              <span className="time-pill">{formatMinutes(e.minutes)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="font-medium" style={{ fontSize: 12.5 }}>{e.tech_name || e.tech_email}</div>
                {e.notes && <div className="text-muted text-sm truncate">{e.notes}</div>}
              </div>
              <span className="text-muted text-sm">{format(parseApiDate(e.logged_at), 'MMM d')}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => deleteEntry(e.id)} style={{ padding: '2px 4px', color: 'var(--text-muted)' }}>
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Email Prefs Panel ─────────────────────────────────────────
function EmailPrefsPanel({ ticketId }) {
  const [prefs, setPrefs]       = useState(null);
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    api.get(`/settings/ticket-prefs/${ticketId}`).then(d => setPrefs(d.prefs));
  }, [ticketId]);

  const toggle = async (key) => {
    const newPrefs = { ...prefs, [key]: !prefs[key] };
    setSaving(true);
    try {
      await api.put(`/settings/ticket-prefs/${ticketId}`, newPrefs);
      setPrefs(newPrefs);
    } catch (err) { toast.error('Failed to save preferences'); }
    finally { setSaving(false); }
  };

  if (!prefs) return null;

  const Switch = ({ k }) => (
    <div style={{
      width: 32, height: 18, borderRadius: 9,
      background: prefs[k] ? 'var(--accent)' : 'var(--border)',
      position: 'relative', cursor: saving ? 'default' : 'pointer', flexShrink: 0,
      transition: 'background 0.2s', opacity: saving ? 0.6 : 1,
    }} onClick={() => !saving && toggle(k)}>
      <div style={{
        position: 'absolute', top: 2, left: prefs[k] ? 16 : 2,
        width: 14, height: 14, borderRadius: '50%',
        background: '#fff', transition: 'left 0.2s',
        boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
      }} />
    </div>
  );

  return (
    <div className="card card-sm">
      <div className="flex justify-between items-center" style={{ marginBottom: 10 }}>
        <div style={{ fontWeight: 600, fontSize: 12.5, color: 'var(--text-secondary)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          <Bell size={12} style={{ display: 'inline', marginRight: 5 }} />Emails
        </div>
        <button onClick={() => toggle('emails_enabled')} className={`btn btn-sm ${prefs.emails_enabled ? 'btn-secondary' : 'btn-primary'}`} style={{ padding: '2px 8px', fontSize: 11 }} disabled={saving}>
          {prefs.emails_enabled ? <><BellOff size={10} /> Off</> : <><Bell size={10} /> On</>}
        </button>
      </div>
      {prefs.emails_enabled && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          {[
            { k: 'notify_client_reply',          l: 'Client reply alerts' },
            { k: 'notify_staff_reply',           l: 'Staff reply alerts' },
            { k: 'notify_client_status_changed', l: 'Status change alerts' },
            { k: 'notify_tech_assigned',         l: 'Assignment alerts' },
          ].map(({ k, l }) => (
            <label key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
              <span style={{ fontSize: 12.5, color: prefs[k] ? 'var(--text-primary)' : 'var(--text-muted)' }}>{l}</span>
              <Switch k={k} />
            </label>
          ))}
        </div>
      )}
      {!prefs.emails_enabled && <p className="text-muted text-sm">All notifications disabled for this ticket.</p>}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────
export default function TicketDetailPage() {
  const { id } = useParams();
  const { user, isStaff, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [ticket, setTicket]     = useState(null);
  const [comments, setComments] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [techs, setTechs]       = useState([]);
  const [orgs, setOrgs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [comment, setComment]   = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [posting, setPosting]   = useState(false);
  const [sla, setSla]           = useState(null);
  const [myRating, setMyRating] = useState(null);
  const [reopening, setReopening] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryVisible, setSummaryVisible] = useState(false);
  const [jumpTo, setJumpTo] = useState('');
  const commentEditorRef = React.useRef(null);

  const fetchTicket = async () => {
    try {
      const { ticket: t } = await api.get(`/tickets/${id}`);
      setTicket(t);
    } catch {
      toast.error('Ticket not found');
      navigate('/tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = () =>
    api.get(`/tickets/${id}/comments`).then(d => setComments(d.comments || []));

  useEffect(() => {
    fetchTicket();
    fetchComments();
    if (isStaff) {
      api.get('/lookups/statuses').then(d => setStatuses(d.statuses || []));
      api.get('/lookups/priorities').then(d => setPriorities(d.priorities || []));
      api.get('/lookups/categories').then(d => setCategories(d.categories || []));
      api.get('/users/techs').then(d => setTechs(d.users || []));
      api.get('/orgs').then(d => setOrgs(d.orgs || []));
    }
  }, [id]);

  // Fetch SLA status after ticket loads
  useEffect(() => {
    if (ticket && isStaff) {
      api.get(`/sla/status/${ticket.id}`).then(d => setSla(d.sla)).catch(() => {});
    }
  }, [ticket?.id]);

  const handleReopen = async () => {
    if (!window.confirm('Reopen this ticket? A new ticket will be created linked to this one.')) return;
    setReopening(true);
    try {
      const { ticket: newTicket, original_ticket_number } = await api.post(`/tickets/${ticket.id}/reopen`, {
        description: `Following up on resolved ticket #${original_ticket_number}.`
      });
      toast.success(`New ticket #${newTicket.ticket_number} created`);
      navigate(`/tickets/${newTicket.ticket_number}`);
    } catch (e) { toast.error(e.message); }
    finally { setReopening(false); }
  };

  const fetchSummary = async () => {
    setSummaryLoading(true);
    setSummaryVisible(true);
    try {
      const d = await api.get(`/tickets/${id}/summary`);
      setSummary(d.summary || 'No summary available (ticket may not have enough content yet).');
    } catch {
      setSummary('Unable to generate summary.');
    } finally {
      setSummaryLoading(false);
    }
  };

  const updateTicket = async (field, value) => {
    try {
      const { ticket: updated } = await api.put(`/tickets/${id}`, { [field]: value || null });
      setTicket(updated);
      toast.success('Updated');
    } catch { toast.error('Update failed'); }
  };

  const canDeleteComments = isSuperAdmin || user?.can_delete_comments;

  const deleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment? This cannot be undone.')) return;
    try {
      await api.delete(`/tickets/${id}/comments/${commentId}`);
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch (err) {
      toast.error(err.message || 'Failed to delete comment');
    }
  };

  const postComment = async () => {
    if (!comment || !comment.trim() || comment === '<p></p>') return;
    setPosting(true);
    try {
      const { comment: c } = await api.post(`/tickets/${id}/comments`, {
        body: comment,
        is_internal: isInternal && isStaff,
      });
      setComments(prev => [...prev, c]);
      setComment('');
      commentEditorRef.current?.commands.clearContent(true);
      setIsInternal(false);
    } catch { toast.error('Failed to post comment'); }
    finally { setPosting(false); }
  };

  const initials = (p) => {
    if (!p) return '?';
    return (p.full_name || p.email || '?').split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase();
  };

  if (loading) return (
    <div>
      <div className="page-header"><h2>Ticket</h2></div>
      <div className="page-body" style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
        <div className="spinner" />
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-3" style={{ minWidth: 0, flex: 1 }}>
          <button className="btn btn-ghost btn-sm no-print" onClick={() => navigate('/tickets')}><ArrowLeft size={15} /></button>
          <h2 className="truncate" style={{ fontSize: 14 }}>
            <span className="ticket-num" style={{ marginRight: 8 }}>#{ticket.ticket_number}</span>
            {ticket.subject}
          </h2>
        </div>
        <div className="flex gap-2 items-center no-print" style={{ flexShrink: 0, marginLeft: 12 }}>
          {ticket.status && <StatusBadge status={ticket.status} />}
          {ticket.priority && <PriorityBadge priority={ticket.priority} />}
          {/* Reopen: clients only, closed ticket */}
          {!isStaff && ticket.status?.is_closed && (
            <button
              className="btn btn-sm"
              onClick={handleReopen}
              disabled={reopening}
              style={{ display: 'flex', alignItems: 'center', gap: 5, marginLeft: 8 }}
            >
              <RotateCcw size={13} /> {reopening ? 'Opening…' : 'Reopen'}
            </button>
          )}
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => window.print()}
            title="Print ticket"
            style={{ display: 'flex', alignItems: 'center', gap: 5 }}
          >
            <Printer size={14} /> Print
          </button>
        </div>
      </div>

      {/* Rating bar for clients on closed tickets */}
      {!isStaff && ticket.status?.is_closed && (
        <div className="no-print" style={{ padding: '10px 20px', borderBottom: '1px solid var(--border)', background: 'var(--surface)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <RatingWidget
            ticketId={ticket.id}
            existingRating={myRating}
            onRated={v => setMyRating(v)}
          />
        </div>
      )}

      {/* Staff: show rating badge if they have permission */}
      {isStaff && user?.can_view_ratings && ticket.rating && (
        <div className="no-print" style={{ padding: '8px 20px', borderBottom: '1px solid var(--border)', background: 'var(--surface)', display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-muted)' }}>
          Client rating:&nbsp;
          {ticket.rating === 'thumbs_up'
            ? <span style={{ color: '#10b981', display:'flex', alignItems:'center', gap:4, fontWeight:600 }}><ThumbsUp size={12}/> Good</span>
            : <span style={{ color: '#ef4444', display:'flex', alignItems:'center', gap:4, fontWeight:600 }}><ThumbsDown size={12}/> Not great</span>
          }
        </div>
      )}

      {/* Body */}
      <div className="page-body">
        {/* Mobile jump nav */}
        <div className="ticket-mobile-jump no-print">
          <select
            className="form-select"
            value={jumpTo}
            onChange={e => {
              const val = e.target.value;
              if (!val) return;
              const el = document.getElementById(val);
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              setJumpTo('');
            }}
          >
            <option value="">Jump to section…</option>
            <option value="ticket-section-details">Ticket Details</option>
            <option value="ticket-section-submitter">Submitted by</option>
            {isStaff && <option value="ticket-section-watchers">Watchers</option>}
            {isStaff && sla?.rule && <option value="ticket-section-sla">SLA Status</option>}
            {isStaff && <option value="ticket-section-time">Time Tracking</option>}
            {ticket?.id && <option value="ticket-section-attachments">Attachments</option>}
            {isStaff && <option value="ticket-section-email-prefs">Email Preferences</option>}
          </select>
        </div>

        <div className="ticket-detail-grid">

          {/* ── Left column ── */}
          <div>
            {/* Print-only metadata block */}
            <div className="print-only" style={{ marginBottom: 20, paddingBottom: 14, borderBottom: '1px solid #ccc' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px 24px', fontSize: 12 }}>
                <div><strong>Status:</strong> {ticket.status?.name || '—'}</div>
                <div><strong>Priority:</strong> {ticket.priority?.name || '—'}</div>
                {ticket.category && <div><strong>Category:</strong> {ticket.category.name}</div>}
                {ticket.assigned_to
                  ? <div><strong>Assigned to:</strong> {ticket.assigned_to.full_name || ticket.assigned_to.email}</div>
                  : <div><strong>Assigned to:</strong> Unassigned</div>
                }
                <div><strong>Submitted by:</strong> {ticket.created_by?.full_name || ticket.created_by?.email || '—'}</div>
                {ticket.created_by?.full_name && ticket.created_by?.email && <div><strong>Email:</strong> {ticket.created_by.email}</div>}
                {ticket.created_by?.phone && <div><strong>Phone:</strong> {ticket.created_by.phone}</div>}
                {ticket.created_by?.company && <div><strong>Company:</strong> {ticket.created_by.company}</div>}
                {ticket.org && <div><strong>Organization:</strong> {ticket.org.name}</div>}
                <div><strong>Created:</strong> {format(parseApiDate(ticket.created_at), 'MMM d, yyyy h:mm a')}</div>
                {ticket.due_date && <div><strong>Due:</strong> {format(parseApiDate(ticket.due_date), 'MMM d, yyyy')}</div>}
              </div>
            </div>

            {/* AI Summary */}
            {isStaff && (
              <div className="no-print" style={{ marginBottom: 16 }}>
                {!summaryVisible ? (
                  <button className="btn btn-ghost btn-sm" onClick={fetchSummary} style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    <Sparkles size={13} /> AI Summary
                  </button>
                ) : (
                  <div style={{
                    background: 'var(--purple-light)', border: '1px solid #e9d5ff',
                    borderRadius: 'var(--radius)', padding: '12px 16px', fontSize: 13,
                  }}>
                    <div className="flex justify-between items-center" style={{ marginBottom: summaryLoading ? 0 : 6 }}>
                      <span style={{ fontWeight: 600, fontSize: 12, color: 'var(--purple)', display: 'flex', alignItems: 'center', gap: 5 }}>
                        <Sparkles size={12} /> AI Summary
                      </span>
                      <div className="flex gap-1">
                        <button className="btn btn-ghost btn-sm" onClick={fetchSummary} disabled={summaryLoading} style={{ padding: '2px 6px', fontSize: 11 }} title="Regenerate">
                          {summaryLoading ? '…' : '↻'}
                        </button>
                        <button className="btn btn-ghost btn-sm" onClick={() => setSummaryVisible(false)} style={{ padding: '2px 6px' }}>
                          <X size={12} />
                        </button>
                      </div>
                    </div>
                    {summaryLoading ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', padding: '8px 0' }}>
                        <div className="spinner" style={{ width: 14, height: 14 }} /> Generating summary…
                      </div>
                    ) : (
                      <p style={{ margin: 0, lineHeight: 1.6, color: 'var(--text-secondary)' }}>{summary}</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Original description */}
            <div className="card mb-4">
              <div className="flex items-center gap-2" style={{ marginBottom: 12 }}>
                <div className="avatar">{initials(ticket.created_by)}</div>
                <div>
                  <div className="font-medium" style={{ fontSize: 13.5 }}>
                    {ticket.created_by?.full_name || ticket.created_by?.email}
                    {ticket.org && <span className="text-muted" style={{ fontWeight: 400 }}> · {ticket.org.name}</span>}
                  </div>
                  <div className="text-muted text-sm">{format(parseApiDate(ticket.created_at), 'MMM d, yyyy h:mm a')}</div>
                </div>
              </div>
              <div style={{ background: 'var(--bg-secondary)', borderRadius: 'var(--radius)', padding: '14px 16px' }}>
                <RichTextDisplay content={ticket.description} />
              </div>
            </div>

            {/* Comments */}
            <div className="card mb-4" style={{ padding: 0 }}>
              <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
                <h3 style={{ fontSize: 14 }}>
                  Replies {comments.length > 0 && <span className="text-muted">({comments.length})</span>}
                </h3>
              </div>

              {comments.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                  No replies yet.
                </div>
              ) : (
                <div style={{ padding: '0 20px' }}>
                  {comments.map(c => (
                    <div key={c.id} className="comment">
                      <div className="avatar" style={{
                        background: c.author?.role === 'client' ? 'var(--blue-light)' : 'var(--accent-light)',
                        color: c.author?.role === 'client' ? 'var(--blue)' : 'var(--accent)',
                      }}>
                        {initials(c.author)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="flex items-center gap-2" style={{ marginBottom: 6 }}>
                          <span className="font-medium" style={{ fontSize: 13 }}>
                            {c.author?.full_name || c.author?.email}
                          </span>
                          <span className="badge" style={{ background: 'var(--bg-secondary)', color: 'var(--text-muted)', padding: '1px 6px', textTransform: 'capitalize' }}>
                            {c.author?.role}
                          </span>
                          {c.is_internal && (
                            <span className="badge" style={{ background: '#fffbeb', color: '#d97706', border: '1px solid #fde68a', padding: '1px 6px' }}>
                              <Lock size={10} /> Internal
                            </span>
                          )}
                          <span className="text-muted text-sm" style={{ marginLeft: 'auto' }}>
                            {formatDistanceToNow(parseApiDate(c.created_at), { addSuffix: true })}
                          </span>
                          {canDeleteComments && (
                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={() => deleteComment(c.id)}
                              title="Delete comment"
                              style={{ padding: '2px 4px', color: 'var(--text-muted)' }}
                            >
                              <Trash2 size={12} />
                            </button>
                          )}
                        </div>
                        <div style={{
                          background: c.is_internal ? '#fffbeb' : 'var(--bg-secondary)',
                          border: `1px solid ${c.is_internal ? '#fde68a' : 'var(--border)'}`,
                          borderRadius: 'var(--radius)', padding: '10px 14px',
                        }}>
                          <RichTextDisplay content={c.body} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Reply box */}
              <div className="no-print" style={{ padding: 20, borderTop: '1px solid var(--border)' }}>
                {isStaff && (
                  <div className="flex gap-2" style={{ marginBottom: 10 }}>
                    <button className={`btn btn-sm ${!isInternal ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setIsInternal(false)}>Reply to client</button>
                    <button className={`btn btn-sm ${isInternal ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setIsInternal(true)}><Lock size={12} /> Internal note</button>
                  </div>
                )}
                <RichTextEditor
                  value={comment}
                  onChange={setComment}
                  editorRef={commentEditorRef}
                  placeholder={isInternal ? 'Internal note — not visible to client…' : 'Write a reply…'}
                  minHeight={90}
                  internalStyle={isInternal ? {
                    background: '#fffbeb',
                    borderColor: '#fde68a',
                  } : undefined}
                />
                <div className="flex justify-between items-center" style={{ marginTop: 8 }}>
                  <div className="flex items-center gap-2">
                    <span className="text-muted text-sm">Ctrl+Enter to send</span>
                    {isStaff && (
                      <CannedResponsePicker onSelect={text => {
                        if (commentEditorRef.current) {
                          commentEditorRef.current.commands.insertContent(text);
                          commentEditorRef.current.commands.focus();
                        }
                      }} />
                    )}
                  </div>
                  <button className="btn btn-primary btn-sm" onClick={postComment} disabled={posting || !comment.trim()}>
                    <Send size={13} /> {posting ? 'Sending…' : 'Send Reply'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* ── Right sidebar ── */}
          <div className="no-print" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Ticket Details */}
            <div id="ticket-section-details" className="card card-sm">
              <div style={{ fontWeight: 600, fontSize: 12.5, marginBottom: 14, color: 'var(--text-secondary)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                Ticket Details
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
                <div>
                  <div className="form-label">Status</div>
                  {isStaff ? (
                    <select className="form-select" value={ticket.status?.id || ''} onChange={e => updateTicket('status_id', e.target.value)}>
                      {statuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  ) : <StatusBadge status={ticket.status} />}
                </div>
                <div>
                  <div className="form-label">Priority</div>
                  {isStaff ? (
                    <select className="form-select" value={ticket.priority?.id || ''} onChange={e => updateTicket('priority_id', e.target.value)}>
                      {priorities.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  ) : <PriorityBadge priority={ticket.priority} />}
                </div>
                {isStaff && (
                  <>
                    <div>
                      <div className="form-label">Category</div>
                      <select className="form-select" value={ticket.category?.id || ''} onChange={e => updateTicket('category_id', e.target.value)}>
                        <option value="">None</option>
                        {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <div className="form-label">Assigned to</div>
                      <select className="form-select" value={ticket.assigned_to?.id || ''} onChange={e => updateTicket('assigned_to', e.target.value)}>
                        <option value="">Unassigned</option>
                        {techs.map(t => <option key={t.id} value={t.id}>{t.full_name || t.email}</option>)}
                      </select>
                    </div>
                    <div>
                      <div className="form-label">Organization</div>
                      <select className="form-select" value={ticket.org?.id || ''} onChange={e => updateTicket('org_id', e.target.value)}>
                        <option value="">None</option>
                        {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                      </select>
                    </div>
                  </>
                )}
                <div>
                  <div className="form-label">Created</div>
                  <div className="text-sm">{format(parseApiDate(ticket.created_at), 'MMM d, yyyy')}</div>
                </div>
                <div>
                  <div className="form-label">Updated</div>
                  <div className="text-sm">{formatDistanceToNow(parseApiDate(ticket.updated_at), { addSuffix: true })}</div>
                </div>
              </div>
            </div>

            {/* Submitted by */}
            <div id="ticket-section-submitter" className="card card-sm">
              <div style={{ fontWeight: 600, fontSize: 12.5, marginBottom: 12, color: 'var(--text-secondary)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                Submitted by
              </div>
              <div className="flex items-center gap-2">
                <div className="avatar">{initials(ticket.created_by)}</div>
                <div style={{ minWidth: 0 }}>
                  <div className="font-medium text-sm truncate">{ticket.created_by?.full_name || ticket.created_by?.email}</div>
                  {ticket.created_by?.company && <div className="text-muted text-sm truncate">{ticket.created_by.company}</div>}
                  {ticket.org && <div className="text-muted text-sm truncate">🏢 {ticket.org.name}</div>}
                </div>
              </div>
            </div>

            {/* Watchers — staff only */}
            {isStaff && <div id="ticket-section-watchers"><WatchersPanel ticketId={id} /></div>}

            {/* SLA status — staff only */}
            {isStaff && sla?.rule && <div id="ticket-section-sla"><SlaPanel sla={sla} /></div>}

            {/* Time tracking — staff only */}
            {isStaff && <div id="ticket-section-time"><TimePanel ticketId={id} /></div>}

            {/* Attachments */}
            {ticket?.id && <div id="ticket-section-attachments"><AttachmentsPanel parentType="ticket" parentId={ticket.id} /></div>}

            {/* Email preferences — staff only */}
            {isStaff && <div id="ticket-section-email-prefs"><EmailPrefsPanel ticketId={id} /></div>}
          </div>
        </div>
      </div>
    </div>
  );
}
